import { css } from '@emotion/css';
import { Notification } from 'is24-corecss';
import { useMemo } from 'react';
import { AragSection } from './AragSection';
import type { InsurancePackageState, InsuranceServiceName } from '@/@types/insurance';
import { InsuranceActivationStatus, InsurancePackageType } from '@/@types/insurance';
import { ProductsStatusKey } from '@/@types/productsStatus';
import { useGetInsuranceDetails, useUpdateInsuranceDetails } from '@/apiHooks/insurance.hook';
import { useGetProductsStatus } from '@/apiHooks/productsStatus.hook';
import { useProductStatus } from '@/customHooks/useProductStatus.hook';
import { useIntl } from '@/i18n/hooks/useIntl';
import { default as Msg } from '@/i18n/components/FormattedMessage';
import { PlusBadge } from '@/components/common/display/plusBadge/PlusBadge';
import { useHasWohnenPlusLight } from '@/customHooks/useGetHasProduct.hook';

const screenSizeS = '576px';
const styles = {
  overviewTab: { maxWidth: 530 },

  sectionContainer: css`
    padding-bottom: 24px;

    @media screen and (min-width: ${screenSizeS}) {
      display: flex;
      justify-content: space-between;
    }
  `,
  sectionTitle: css`
    font-size: 14px;
    font-weight: bold;
    padding-bottom: 8px;

    @media screen and (min-width: ${screenSizeS}) {
      width: 50%;
      padding-bottom: initial;
    }
  `,
  sectionText: css`
    font-size: 14px;
    display: flex;
    flex-direction: column;
    @media screen and (min-width: ${screenSizeS}) {
      width: 50%;
    }
  `,
};

export const RenderSection = ({ title, content }: { title: string; content: React.JSX.Element }) => (
  <div className={styles.sectionContainer}>
    <p className={styles.sectionTitle}>{title}</p>
    <div className={styles.sectionText}>{content}</div>
  </div>
);

const getBullet3Key = ({ hasWohnenPlusLight, serviceName }: { hasWohnenPlusLight: boolean; serviceName: string }) => {
  if (!hasWohnenPlusLight) {
    return `benefits.${serviceName}.modal.overview.notEntitled.bullet3`;
  }

  return `benefits.${serviceName}.modal.overview.notEntitled.bullet3Light`;
};

const OverviewTab = ({
  serviceName,
  isEntitled,
  additionalParagraph,
}: {
  serviceName: InsuranceServiceName;
  isEntitled: boolean;
  additionalParagraph?: boolean;
}) => {
  const { t } = useIntl();
  const { insuranceDetails, isInsuranceDetailsSuccess } = useGetInsuranceDetails(isEntitled);
  const { productsStatus } = useGetProductsStatus();
  const serviceNamelowerCase = serviceName.toLowerCase();
  const insurance = useProductStatus(ProductsStatusKey.insurancePackage) as InsurancePackageState;
  const { isError: isUpdateDetailsError } = useUpdateInsuranceDetails(
    insuranceDetails ? insuranceDetails!.insuranceNumber : '',
  );

  const showStatusMessage = useMemo(() => {
    const pending = productsStatus?.insurancePackage?.status === InsuranceActivationStatus.PENDING;
    const missingDetails = !insuranceDetails?.user?.firstName || !insuranceDetails?.user?.lastName;

    if (isUpdateDetailsError) return { updateDetailsError: true, pendingError: false, message: '' };
    if (pending)
      return {
        updateDetailsError: false,
        pendingError: isInsuranceDetailsSuccess,
        message: missingDetails ? 'missingDetails' : 'pendingError',
      };
  }, [productsStatus, isUpdateDetailsError, insuranceDetails, isInsuranceDetailsSuccess]);

  const AdditionalParagraph = () => {
    if (additionalParagraph && insurance.type === InsurancePackageType.PACKAGE_M) {
      return (
        <Msg
          id={`benefits.${serviceNamelowerCase}.modal.overview.entitled.info150`}
          defaultMessage='Hierfür wird eine Selbstbeteiligung von 150€ erforderlich.'
        />
      );
    }

    if (additionalParagraph && insurance.type === InsurancePackageType.PACKAGE_L) {
      return (
        <Msg
          id={`benefits.${serviceNamelowerCase}.modal.overview.entitled.infoFree`}
          defaultMessage='Dieser Service ist für dich kostenlos.'
        />
      );
    }

    return <></>;
  };

  const hasWohnenPlusLight = useHasWohnenPlusLight();
  const bullet3Key = getBullet3Key({
    hasWohnenPlusLight,
    serviceName: serviceNamelowerCase,
  });

  if (!isEntitled) {
    return (
      <div style={styles.overviewTab}>
        <div className='font-heading-xsmall-regular padding-bottom-l'>
          <Msg id={`benefits.${serviceNamelowerCase}.modal.overview.notEntitled.paragraph1`} defaultMessage='' />
        </div>
        <div className='padding-bottom-l'>
          <Msg id={`benefits.${serviceNamelowerCase}.modal.overview.notEntitled.paragraph2`} defaultMessage='' />
        </div>
        <ul className='list-check list-spacing-s'>
          <li>
            <Msg id={`benefits.${serviceNamelowerCase}.modal.overview.notEntitled.bullet1`} defaultMessage='' />
          </li>
          <li>
            <Msg id={`benefits.${serviceNamelowerCase}.modal.overview.notEntitled.bullet2`} defaultMessage='' />
          </li>
          <li>
            <Msg
              id={bullet3Key}
              defaultMessage=''
              values={{
                BuyerPlusLogo: <PlusBadge variant='living' />,
              }}
            />
          </li>
        </ul>
      </div>
    );
  }

  return (
    <div style={styles.overviewTab}>
      {showStatusMessage?.pendingError && (
        <Notification
          title={t({ id: `common.aragModal.statusMessage.${showStatusMessage?.message}.headline` })}
          message={t({ id: `common.aragModal.statusMessage.${showStatusMessage?.message}.message` })}
          type='info'
        ></Notification>
      )}
      <div className='margin-bottom-l' />
      <div className='padding-bottom-l font-heading-xsmall-regular'>
        <Msg id={`benefits.${serviceNamelowerCase}.modal.overview.entitled.paragraph1`} defaultMessage='' />
      </div>
      <div>
        <Msg id={`benefits.${serviceNamelowerCase}.modal.overview.entitled.paragraph2`} defaultMessage='' />
      </div>
      <AdditionalParagraph />
      {showStatusMessage?.updateDetailsError && (
        <div className='margin-bottom-m margin-top-m'>
          <Notification
            title={t({ id: 'common.aragModal.statusMessage.updateDetailsError.headline' })}
            message={t({ id: 'common.aragModal.statusMessage.updateDetailsError.message' })}
            type='error'
          ></Notification>
        </div>
      )}
      <AragSection serviceName={serviceName} />
    </div>
  );
};

export default OverviewTab;
